import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import projectsService from '../services/projectsService';
import commercialService from '../services/commercialService';
import timesheetService from '../services/timesheetService';
import timeOffService from '../services/timeOffService';
import { Office, Opportunity, Project, PublicHoliday, TimeOffRequest, User } from '../types';
import {
  daysUntil,
  formatHours,
  getLoadRisk,
  getPmPressureRisk,
  getRiskColor,
  getUpcomingOpportunities,
  getWeightedOpportunityHours,
  isActiveProject,
} from '../utils/commercialPlanning';
import { ROLE } from '../utils/roles';

const HOURS_PER_WORKDAY = 8;
const DAYS_WINDOW = 90;
const TARGET_DEPARTMENTS = ['Engineering', 'IT'] as const;
const STAFF_COUNT_ROLES = [ROLE.PM, ROLE.ENG, ROLE.LEAD] as const;

type CapacityDepartment = (typeof TARGET_DEPARTMENTS)[number];

interface TimesheetReportRow {
  project: string;
  hours: number;
  workDate?: string;
  user?: string;
}

interface OfficeHoliday extends PublicHoliday {
  office?: string;
}

const normalizeDepartmentName = (departmentName?: string): CapacityDepartment | null => {
  if (!departmentName) return null;
  if (departmentName === 'Engineering') return 'Engineering';
  if (departmentName === 'IT') return 'IT';
  return null;
};

const CapacitySnapshotPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? '#0f2134' : '#ffffff';

  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [timesheetRows, setTimesheetRows] = useState<TimesheetReportRow[]>([]);
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
  const [publicHolidays, setPublicHolidays] = useState<OfficeHoliday[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - DAYS_WINDOW);
        const forecastEndDate = new Date();
        forecastEndDate.setDate(endDate.getDate() + DAYS_WINDOW);
        const years = Array.from(new Set([endDate.getFullYear(), forecastEndDate.getFullYear()]));

        const [projectData, userData, officeData, reportData, requestData, holidayDataPerYear] = await Promise.all([
          projectsService.getProjects(),
          projectsService.getUsers(),
          projectsService.getOffices(),
          timesheetService.getTimesheetReport({
            start_date: startDate.toISOString().slice(0, 10),
            end_date: endDate.toISOString().slice(0, 10),
          }),
          timeOffService.getAllTimeOffRequests(),
          Promise.all(years.map((year) => timeOffService.getPublicHolidays(year))),
        ]);
        setProjects(projectData);
        setUsers(userData);
        setOffices(officeData);
        setOpportunities(commercialService.listOpportunities());
        setTimesheetRows(Array.isArray(reportData) ? reportData : []);
        setTimeOffRequests(requestData);
        setPublicHolidays(holidayDataPerYear.flat() as OfficeHoliday[]);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('Failed to load capacity data');
      }
    };

    loadData();
  }, []);

  const activeProjects = useMemo(() => projects.filter(isActiveProject), [projects]);
  const upcomingOpportunities = useMemo(() => getUpcomingOpportunities(opportunities, DAYS_WINDOW), [opportunities]);
  const officesById = useMemo(() => new Map(offices.map((office) => [office.id, office])), [offices]);
  const departmentByUserId = useMemo(
    () => new Map(users.map((user) => [user.id, normalizeDepartmentName(user.department_name)])),
    [users],
  );
  const now = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);
  const forecastEnd = useMemo(() => {
    const date = new Date(now);
    date.setDate(date.getDate() + DAYS_WINDOW);
    return date;
  }, [now]);
  const activeProjectsByName = useMemo(
    () => new Map(activeProjects.map((project) => [project.name.trim().toLowerCase(), project])),
    [activeProjects],
  );

  const loggedHoursByProjectName = useMemo(() => {
    const totals = new Map<string, number>();

    timesheetRows.forEach((row) => {
      const projectKey = row.project?.trim().toLowerCase();
      if (!projectKey) return;
      totals.set(projectKey, (totals.get(projectKey) || 0) + (row.hours || 0));
    });

    return totals;
  }, [timesheetRows, activeProjectsByName]);

  const approvedTimeOffByUser = useMemo(() => {
    const totals = new Map<number, number>();

    timeOffRequests
      .filter((request) => request.status === 'approved')
      .forEach((request) => {
        const requestDate = new Date(`${request.date}T00:00:00`);
        if (requestDate < now || requestDate > forecastEnd) return;

        const durationHours = request.holidayType === 'half_day' ? HOURS_PER_WORKDAY / 2 : HOURS_PER_WORKDAY;
        totals.set(request.userId, (totals.get(request.userId) || 0) + durationHours);
      });

    return totals;
  }, [forecastEnd, now, timeOffRequests]);

  const holidayDatesByOfficeCountry = useMemo(() => {
    const datesMap = new Map<string, Set<string>>();

    publicHolidays.forEach((holiday) => {
      const officeCountry = holiday.office;
      if (!officeCountry) return;
      if (!datesMap.has(officeCountry)) {
        datesMap.set(officeCountry, new Set<string>());
      }
      datesMap.get(officeCountry)?.add(holiday.date);
    });

    return datesMap;
  }, [publicHolidays]);

  const grossCapacityByDepartment = useMemo(() => {
    const capacityMap = new Map<CapacityDepartment, number>([
      ['Engineering', 0],
      ['IT', 0],
    ]);

    users
      .filter((user) => user.user_office && (user.roles.includes(ROLE.PM) || user.roles.includes(ROLE.ENG) || user.roles.includes(ROLE.LEAD)))
      .forEach((user) => {
        const department = normalizeDepartmentName(user.department_name);
        if (!department) return;
        const office = officesById.get(user.user_office as number);
        const officeCountry = office?.country;
        const holidayDates = officeCountry ? holidayDatesByOfficeCountry.get(officeCountry) : undefined;

        let workingHours = 0;
        const cursor = new Date(now);

        while (cursor <= forecastEnd) {
          const isoDate = cursor.toISOString().slice(0, 10);
          const isWeekend = cursor.getDay() === 0 || cursor.getDay() === 6;
          const isHoliday = holidayDates?.has(isoDate);

          if (!isWeekend && !isHoliday) {
            workingHours += HOURS_PER_WORKDAY;
          }

          cursor.setDate(cursor.getDate() + 1);
        }

        const approvedLeaveHours = approvedTimeOffByUser.get(user.id) || 0;
        const grossHours = Math.max(workingHours - approvedLeaveHours, 0);
        capacityMap.set(department, (capacityMap.get(department) || 0) + grossHours);
      });

    return capacityMap;
  }, [approvedTimeOffByUser, forecastEnd, holidayDatesByOfficeCountry, now, officesById, users]);

  const departmentRows = useMemo(() => {
    const departmentStats = new Map<CapacityDepartment, { staffCount: number; grossCapacity: number; spent: number; remaining: number; probable: number }>([
      ['Engineering', { staffCount: 0, grossCapacity: grossCapacityByDepartment.get('Engineering') || 0, spent: 0, remaining: 0, probable: 0 }],
      ['IT', { staffCount: 0, grossCapacity: grossCapacityByDepartment.get('IT') || 0, spent: 0, remaining: 0, probable: 0 }],
    ]);

    users
      .filter((user) => user.user_office && (user.roles.includes(ROLE.PM) || user.roles.includes(ROLE.ENG) || user.roles.includes(ROLE.LEAD)))
      .forEach((user) => {
        const department = normalizeDepartmentName(user.department_name);
        if (!department) return;
        const current = departmentStats.get(department);
        if (!current) return;
        current.staffCount += 1;
        current.grossCapacity = grossCapacityByDepartment.get(department) || current.grossCapacity;
      });

    activeProjects.forEach((project) => {
      const pmDepartment = project.pm_id ? departmentByUserId.get(project.pm_id) : null;
      const engineerDepartmentCounts = (project.engineers || []).reduce(
        (counts, engineer) => {
          const engineerDepartment = normalizeDepartmentName(engineer.department_name);
          if (engineerDepartment) {
            counts[engineerDepartment] += 1;
          }
          return counts;
        },
        { Engineering: 0, IT: 0 } as Record<CapacityDepartment, number>,
      );
      const fallbackDepartment =
        engineerDepartmentCounts.Engineering === 0 && engineerDepartmentCounts.IT === 0
          ? null
          : engineerDepartmentCounts.Engineering >= engineerDepartmentCounts.IT
            ? 'Engineering'
            : 'IT';
      const department = pmDepartment || fallbackDepartment;
      if (!department) return;
      const current = departmentStats.get(department);
      if (!current) return;
      const projectKey = project.name.trim().toLowerCase();
      const spentHours = loggedHoursByProjectName.get(projectKey) || 0;
      current.spent += spentHours;
      current.remaining += Math.max((project.manhours || 0) - spentHours, 0);
    });

    upcomingOpportunities.forEach((opportunity) => {
      const department =
        (opportunity.proposed_pm_id ? departmentByUserId.get(opportunity.proposed_pm_id) : null)
        || (opportunity.commercial_owner_id ? departmentByUserId.get(opportunity.commercial_owner_id) : null);
      if (!department) return;
      const current = departmentStats.get(department);
      if (!current) return;
      current.probable += getWeightedOpportunityHours(opportunity);
    });

    return [...departmentStats.entries()].map(([department, value]) => {
      const capacity = value.grossCapacity;
      const total = value.remaining + value.probable;
      const headroom = capacity - total;
      const ratio = capacity > 0 ? total / capacity : total > 0 ? 1 : 0;
      return {
        key: department,
        department,
        staffCount: value.staffCount,
        grossCapacity: value.grossCapacity,
        spent: value.spent,
        remaining: value.remaining,
        probable: value.probable,
        capacity,
        headroom,
        ratio,
        risk: getLoadRisk(ratio),
      };
    }).sort((a, b) => b.ratio - a.ratio);
  }, [activeProjects, departmentByUserId, grossCapacityByDepartment, loggedHoursByProjectName, upcomingOpportunities, users]);

  useEffect(() => {
    const isRoleEligible = (roles: string[]) => STAFF_COUNT_ROLES.some((role) => roles.includes(role));

    const staffSourceRows = users
      .filter((user) => user.user_office && isRoleEligible(user.roles))
      .map((user) => ({
        id: user.id,
        name: user.name,
        departmentName: user.department_name,
        normalizedDepartment: normalizeDepartmentName(user.department_name),
        hasOffice: Boolean(user.user_office),
        roles: user.roles.join(', '),
      }));
    const excludedUserRows = users
      .filter((user) => !(user.user_office && isRoleEligible(user.roles) && normalizeDepartmentName(user.department_name)))
      .map((user) => {
        const reasons: string[] = [];
        if (!user.user_office) reasons.push('missing office');
        if (!isRoleEligible(user.roles)) reasons.push(`role not eligible (${STAFF_COUNT_ROLES.join('/')})`);
        if (!normalizeDepartmentName(user.department_name)) reasons.push('unsupported department (must be Engineering/IT)');
        return {
          id: user.id,
          name: user.name,
          departmentName: user.department_name,
          hasOffice: Boolean(user.user_office),
          roles: user.roles.join(', '),
          excludedReason: reasons.join('; '),
        };
      });
    const usersByName = users.reduce((map, user) => {
      const key = user.name.trim().toLowerCase();
      const bucket = map.get(key) || [];
      bucket.push(user);
      map.set(key, bucket);
      return map;
    }, new Map<string, User[]>());
    const spentByUser = timesheetRows.reduce((map, row) => {
      const key = (row.user || '(unknown)').trim();
      if (!key) return map;
      const current = map.get(key) || { spentHours: 0, entries: 0 };
      current.spentHours += row.hours || 0;
      current.entries += 1;
      map.set(key, current);
      return map;
    }, new Map<string, { spentHours: number; entries: number }>());
    const spentPerUserRows = [...spentByUser.entries()]
      .map(([userName, value]) => {
        const matchingUsers = usersByName.get(userName.toLowerCase()) || [];
        const matchedUser = matchingUsers[0];
        const office = matchedUser?.user_office ? officesById.get(matchedUser.user_office as number) : undefined;
        return {
          user: userName,
          spentHours: value.spentHours,
          entries: value.entries,
          office: office?.name || office?.country || (matchedUser?.user_office ? `Office #${matchedUser.user_office}` : 'No office'),
          matchedUsers: matchingUsers.length,
        };
      })
      .sort((a, b) => b.spentHours - a.spentHours);

    console.groupCollapsed('[CapacitySnapshot debug] departmentRows + staffCount source');
    console.table(
      departmentRows.map((row) => ({
        department: row.department,
        staffCount: row.staffCount,
        capacity: row.capacity,
        spent: row.spent,
        remaining: row.remaining,
        probable: row.probable,
      })),
    );
    console.info('[CapacitySnapshot debug] included users in staffCount filter');
    console.table(staffSourceRows);
    console.info('[CapacitySnapshot debug] excluded users and reasons');
    console.table(excludedUserRows);
    console.info('[CapacitySnapshot debug] spent hours per user with office');
    console.table(spentPerUserRows);
    console.groupEnd();
  }, [departmentRows, officesById, timesheetRows, users]);

  const pmRows = useMemo(() => {
    const pmUsers = users.filter((user) => user.roles.includes(ROLE.PM) || user.roles.includes(ROLE.DIRECTOR) || user.roles.includes(ROLE.ADMIN));

    return pmUsers.map((user) => {
      const managedProjects = activeProjects.filter((project) => project.pm_id === user.id);
      const managedOpportunities = upcomingOpportunities.filter((opportunity) => opportunity.proposed_pm_id === user.id);
      const weightedOpportunityCount = managedOpportunities.reduce((sum, item) => sum + item.probability_to_win / 100, 0);
      const pressureScore = managedProjects.length + weightedOpportunityCount;
      const nextBidDelta = managedOpportunities
        .map((item) => daysUntil(item.bid_due_date))
        .filter((value): value is number => value !== null)
        .sort((a, b) => a - b)[0];

      return {
        name: user.name,
        activeProjects: managedProjects.length,
        upcomingOpportunities: managedOpportunities.length,
        loggedHours90d: managedProjects.reduce((sum, project) => {
          const projectKey = project.name.trim().toLowerCase();
          return sum + (loggedHoursByProjectName.get(projectKey) || 0);
        }, 0),
        pressureScore,
        nextBidDelta,
        risk: getPmPressureRisk(pressureScore),
      };
    }).filter((row) => row.activeProjects > 0 || row.upcomingOpportunities > 0);
  }, [activeProjects, upcomingOpportunities, users, loggedHoursByProjectName]);

  const riskyOpportunities = useMemo(
    () => upcomingOpportunities.filter((item) => {
      const hasSupportedDepartment =
        (item.proposed_pm_id ? departmentByUserId.get(item.proposed_pm_id) : null)
        || (item.commercial_owner_id ? departmentByUserId.get(item.commercial_owner_id) : null);
      return !hasSupportedDepartment || !item.proposed_pm_id || (daysUntil(item.next_action_date) ?? 1) <= 0;
    }),
    [departmentByUserId, upcomingOpportunities],
  );

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
          Department Watch
        </Typography>
        <Typography variant="body2" color="text.secondary">
          90-day readiness view for Engineering and IT using 8h workdays, public holidays, approved leave, remaining project effort, and weighted opportunity demand.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card elevation={0} sx={{ border: `1px solid ${panelBorder}`, bgcolor: surfaceBg }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${panelBorder}` }}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.05em' }}>
                Department Capacity
              </Typography>
            </Box>
            <CardContent>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Department</strong></TableCell>
                    <TableCell><strong>Available Staff</strong></TableCell>
                    <TableCell><strong>Gross 90d Capacity</strong></TableCell>
                    <TableCell><strong>Spent 90d</strong></TableCell>
                    <TableCell><strong>Remaining Load</strong></TableCell>
                    <TableCell><strong>Weighted Pipeline</strong></TableCell>
                    <TableCell><strong>Headroom</strong></TableCell>
                    <TableCell><strong>Risk</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {departmentRows.map((row) => (
                    <TableRow key={row.key} hover>
                      <TableCell>{row.department}</TableCell>
                      <TableCell>{row.staffCount}</TableCell>
                      <TableCell>{formatHours(row.capacity)}</TableCell>
                      <TableCell>{formatHours(row.spent)}</TableCell>
                      <TableCell>{formatHours(row.remaining)}</TableCell>
                      <TableCell>{formatHours(row.probable)}</TableCell>
                      <TableCell>{formatHours(row.headroom)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={getRiskColor(row.risk)}
                          label={`${Math.round(row.ratio * 100)}%`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card elevation={0} sx={{ border: `1px solid ${panelBorder}`, bgcolor: surfaceBg, height: '100%' }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${panelBorder}` }}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.05em' }}>
                PM Pressure
              </Typography>
            </Box>
            <CardContent>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>PM</strong></TableCell>
                    <TableCell><strong>Active Projects</strong></TableCell>
                    <TableCell><strong>Upcoming Opportunities</strong></TableCell>
                    <TableCell><strong>Spent 90d</strong></TableCell>
                    <TableCell><strong>Next Bid</strong></TableCell>
                    <TableCell><strong>Pressure</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pmRows.map((row) => (
                    <TableRow key={row.name} hover>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.activeProjects}</TableCell>
                      <TableCell>{row.upcomingOpportunities}</TableCell>
                      <TableCell>{formatHours(row.loggedHours90d)}</TableCell>
                      <TableCell>{row.nextBidDelta === undefined ? '-' : `${row.nextBidDelta}d`}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={getRiskColor(row.risk)}
                          label={row.pressureScore.toFixed(1)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card elevation={0} sx={{ border: `1px solid ${panelBorder}`, bgcolor: surfaceBg, height: '100%' }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${panelBorder}` }}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.05em' }}>
                Readiness Gaps
              </Typography>
            </Box>
            <CardContent>
              {riskyOpportunities.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  All upcoming opportunities have an assigned PM, a mapped Engineering/IT department, and a future next action date.
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {riskyOpportunities.map((opportunity) => (
                    <Box key={opportunity.id} sx={{ border: `1px solid ${panelBorder}`, borderRadius: 1, p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {opportunity.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {opportunity.client_name || 'No client'}
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {!((opportunity.proposed_pm_id && departmentByUserId.get(opportunity.proposed_pm_id)) || (opportunity.commercial_owner_id && departmentByUserId.get(opportunity.commercial_owner_id))) && (
                          <Chip size="small" label="No Engineering/IT department" color="warning" variant="outlined" />
                        )}
                        {!opportunity.proposed_pm_id && <Chip size="small" label="No PM" color="warning" variant="outlined" />}
                        {(daysUntil(opportunity.next_action_date) ?? 1) <= 0 && (
                          <Chip size="small" label="Action due" color="error" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CapacitySnapshotPage;
